import React, { useState, useEffect, useContext } from 'react';
import { createUser, updateUser, getSteps, updateUserStep, getPatientGroups, assignPatientToGroup, unassignPatientFromGroup } from '../api/api';
import '../index.css';
import './UserForm.css';
import { AuthContext } from '../context/AuthContext';
import Form from './common/Form';
import Input from './common/Input';
import Select from './common/Select';
import Button from './common/Button';
import Error from './common/Error';

const UserForm = ({ user, onClose, onUserUpdated, onUserCreated }) => {
    const { userRole } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        phone: '',
        id_number: '',
        role: 'Paciente',
        step: '1',
        group_ids: [] // Store array of group IDs
    });
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState("");
    const [steps, setSteps] = useState([]);
    const [groups, setGroups] = useState([]); // Available groups
    const [loading, setLoading] = useState(true);

    const canCreateDirector = userRole === "Director";

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const [stepsData, groupsData] = await Promise.all([
                    getSteps(),
                    getPatientGroups()
                ]);
                setSteps(stepsData || []);
                setGroups(groupsData || []);

                if (user) {
                    setIsEditing(true);
                    // Ensure group_ids is initialized properly even if missing from user object
                    setFormData({ ...user, group_ids: user.group_ids || [], step: String(user.step || '1') });
                }
            } catch (err) {
                setError(err.message || 'Error al cargar datos');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleGroupChange = (groupId) => {
        const currentGroups = formData.group_ids || [];
        if (currentGroups.includes(groupId)) {
            setFormData({ ...formData, group_ids: currentGroups.filter(id => id !== groupId) });
        } else {
            setFormData({ ...formData, group_ids: [...currentGroups, groupId] });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setMessage("");

        // Basic validation
        if (!formData.email || !formData.first_name || !formData.last_name) {
            setError("Por favor completa los campos obligatorios.");
            return;
        }

        try {
            let targetUserEmail = formData.email;

            if (isEditing) {
                // Explicitly call update step if needed
                if (user.step !== formData.step) {
                    await updateUserStep(formData.email, formData.step);
                }

                const updatedUser = await updateUser(formData.email, { ...formData });
                if (updatedUser) {
                    onUserUpdated(updatedUser);
                }
                setMessage("Usuario actualizado correctamente");
            } else {
                const newUser = await createUser(formData);
                targetUserEmail = newUser.email; // Ensure we have the correct email/ID
                onUserCreated(newUser);
                setMessage("Usuario creado correctamente");
            }

            // --- Handle Group Assignments Manually ---
            // 1. Get current groups (for diffing) or just use the difference 
            //    between what user HAD and what formData HAS.
            //    However, for a new user, 'user' prop is null/undefined.

            const originalGroupIds = user?.group_ids || [];
            const newGroupIds = formData.group_ids || [];

            // Groups to Add: Present in new, missing in original
            const toAdd = newGroupIds.filter(id => !originalGroupIds.includes(id));

            // Groups to Remove: Present in original, missing in new
            const toRemove = originalGroupIds.filter(id => !newGroupIds.includes(id));

            // Execute API calls
            // We use Promise.allSettled to try all, even if some fail
            const assignmentPromises = [
                ...toAdd.map(groupId => assignPatientToGroup(groupId, targetUserEmail)),
                ...toRemove.map(groupId => unassignPatientFromGroup(groupId, targetUserEmail))
            ];

            await Promise.all(assignmentPromises);

            setTimeout(() => onClose(), 1500);

        } catch (err) {
            console.error(err);
            setError(err.message || 'Error al procesar el usuario');
        }
    };

    if (loading) return <div className="loading-spinner">Cargando formulario...</div>;

    return (
        <div className="user-form-card">
            <div className="form-header">
                <h2>{isEditing ? '✏️ Editar Usuario' : '✨ Nuevo Usuario'}</h2>
                <button className="close-btn" onClick={onClose}>&times;</button>
            </div>

            <form className='form-user' onSubmit={handleSubmit}>
                {message && <div className='container-message success'>✅ {message}</div>}
                {error && <div className='container-message error'>❌ {error}</div>}

                <div className="form-grid">
                    <div className="form-section">
                        <h3>Información Personal</h3>
                        <div className="form-group-custom">
                            <label>Email (ID de usuario)</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} className="custom-input" disabled={isEditing} required />
                        </div>

                        {!isEditing && (
                            <div className="form-group-custom">
                                <label>Contraseña</label>
                                <input type="password" name="password" value={formData.password} onChange={handleChange} className="custom-input" required />
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group-custom">
                                <label>Nombre</label>
                                <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="custom-input" required />
                            </div>
                            <div className="form-group-custom">
                                <label>Apellido</label>
                                <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} className="custom-input" required />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group-custom">
                                <label>Apellido Materno</label>
                                <input type="text" name="middle_name" value={formData.middle_name} onChange={handleChange} className="custom-input" />
                            </div>
                            <div className="form-group-custom">
                                <label>Cédula / ID</label>
                                <input type="text" name="id_number" value={formData.id_number} onChange={handleChange} className="custom-input" />
                            </div>
                        </div>

                        <div className="form-group-custom">
                            <label>Teléfono</label>
                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="custom-input" required />
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Configuración</h3>
                        <div className="form-group-custom">
                            <label>Rol en la plataforma</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                className="custom-select"
                                required
                            >
                                <option value="Paciente">Paciente</option>
                                <option value="Lector">Lector</option>
                                <option value="Navegador">Navegador</option>
                                {canCreateDirector && <option value="Director">Director</option>}
                            </select>
                        </div>

                        <div className="form-group-custom">
                            <label>Paso / Etapa del Paciente</label>
                            <select
                                name="step"
                                value={formData.step}
                                onChange={handleChange}
                                className="custom-select"
                            >
                                <option value="">Seleccione una etapa...</option>
                                {steps.map(step => (
                                    <option key={step.sequence_number} value={step.sequence_number}>
                                        {step.name} (Step {step.sequence_number})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {formData.role === 'Paciente' && (
                            <div className="form-group-custom">
                                <label>Grupos de Interés</label>
                                <div className="groups-list">
                                    {groups.length === 0 ?
                                        <span className="text-muted">No hay grupos disponibles</span> :
                                        groups.map(group => (
                                            <label key={group._id} className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    checked={(formData.group_ids || []).includes(group._id)}
                                                    onChange={() => handleGroupChange(group._id)}
                                                />
                                                <span className="checkbox-label">{group.name}</span>
                                            </label>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                    <button type='button' onClick={onClose} className="btn-secondary">Cancelar</button>
                    <button type="submit" className="btn-primary">
                        {isEditing ? '💾 Guardar Cambios' : '🚀 Crear Usuario'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserForm;
